import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '@/store';
import { User } from '@/types';
import { USER_ROLES, ROLE_ID_MAPPING } from '@/utils/constants';
import { logoutUser } from '@/store/slices/authSlice';

export const useAuth = () => {
  const dispatch = useDispatch();
  const { user, token, isAuthenticated, isLoading } = useSelector((state: RootState) => state.auth);

  const logout = () => {
    dispatch(logoutUser());
  };

  const getUserRoleName = (): string => {
    if (!user?.role_id) return USER_ROLES.USER;
    return ROLE_ID_MAPPING[user.role_id as keyof typeof ROLE_ID_MAPPING] || USER_ROLES.USER;
  };

  const hasRole = (role: string): boolean => {
    const userRoleName = getUserRoleName();
    return userRoleName === role;
  };

  const hasAnyRole = (roles: string[]): boolean => {
    if (!user?.role_id) return false;
    const userRoleName = getUserRoleName();
    return roles.includes(userRoleName);
  };

  const isSuperAdmin = (): boolean => hasRole(USER_ROLES.SUPER_ADMIN);
  const isAdmin = (): boolean => hasRole(USER_ROLES.ADMIN);
  const isStaff = (): boolean => hasRole(USER_ROLES.STAFF);
  const isUser = (): boolean => hasRole(USER_ROLES.USER);

  const canAccessAdmin = (): boolean => {
    return isSuperAdmin() || isAdmin() || isStaff();
  };

  const canAccessBuyer = (): boolean => {
    // Only regular users can access buyer features
    return isUser();
  };

  const canAccessSeller = (): boolean => {
    // Regular users and admins can access seller features
    return isUser() || isAdmin() || isSuperAdmin();
  };

  const canAccessSuperAdmin = (): boolean => {
    return isSuperAdmin();
  };

  const canAccessAdminPanel = (): boolean => {
    return isSuperAdmin() || isAdmin() || isStaff();
  };

  const getDashboardRoute = (): string => {
    // Always return /dashboard - the dashboard will show role-appropriate content
    return '/dashboard';
  };

  const getDisplayName = (): string => {
    if (!user) return '';
    const firstName = user.first_name || '';
    const lastName = user.last_name || '';
    return `${firstName} ${lastName}`.trim() || user.email || '';
  };

  const getInitials = (): string => {
    if (!user) return '';
    const firstName = user.first_name || '';
    const lastName = user.last_name || '';
    if (firstName || lastName) {
      return `${firstName.charAt(0) || ''}${lastName.charAt(0) || ''}`.toUpperCase() || '?';
    }
    return user.email?.charAt(0).toUpperCase() || '?';
  };

  return {
    user,
    token,
    isAuthenticated,
    isLoading,
    logout,
    getUserRoleName,
    hasRole,
    hasAnyRole,
    isSuperAdmin,
    isAdmin,
    isStaff,
    isUser,
    canAccessAdmin,
    canAccessBuyer,
    canAccessSeller,
    canAccessSuperAdmin,
    canAccessAdminPanel,
    getDashboardRoute,
    getDisplayName,
    getInitials,
  };
};
